import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  apiError,
  useAddAdjustmentMutation,
  useAdjustmentsQuery,
  useClosePeriodMutation,
  useCloseStatusQuery,
  useRemoveAdjustmentMutation,
  useStatementQuery,
} from '@/store/api'
import type { AdjustmentKind, CloseStatusRow } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PeriodSelect, recentPeriods } from '@/components/ui/period-select'
import { Table, THead, Th, Tr, Td, SkeletonRows } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { StatementView } from '@/components/panels/StatementView'
import { fmtDateTime, fmtMoney, fmtNum, fmtPeriod } from '@/lib/format'

const KIND_LABELS: Record<AdjustmentKind, string> = {
  service_credit: 'Service credit (8.6)',
  goodwill: 'Goodwill credit',
  restatement: 'Restatement',
  other: 'Other',
}

/**
 * Month-end close: freeze each brand's figures and currency rate for a Billing Period and
 * produce the statement ops invoices from. Adjustments go on before close.
 */
export function MonthEndClose() {
  const [period, setPeriod] = useState(() => recentPeriods(2)[1])
  const { data, isLoading, isError, refetch } = useCloseStatusQuery(period)
  const [close, { isLoading: closing }] = useClosePeriodMutation()
  const [confirmAll, setConfirmAll] = useState(false)
  const [viewing, setViewing] = useState<CloseStatusRow | null>(null)
  const [adjusting, setAdjusting] = useState<CloseStatusRow | null>(null)
  const isCurrent = period === recentPeriods(1)[0]
  const open = data?.filter((r) => !r.closed) ?? []

  const run = async (slug?: string) => {
    try {
      const res = await close({ period, slug }).unwrap()
      const failed = res.results.filter((r) => !r.ok)
      if (failed.length) toast.error(failed.map((f) => `${f.slug}: ${f.error}`).join('\n'))
      else toast.success(`${fmtPeriod(period)} closed for ${res.results.length} brand${res.results.length === 1 ? '' : 's'}`)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <>
      <PageHeader
        title="Month-end close"
        description={
          <>
            Closing a <Term id="billing-period">Billing Period</Term> fixes its currency rate, freezes every figure and produces the
            statement. Anything that changes a closed period later appears on the next statement as relating to it.
          </>
        }
        actions={
          <>
            <PeriodSelect value={period} onChange={setPeriod} />
            <Button disabled={isCurrent || open.length === 0} loading={closing} onClick={() => setConfirmAll(true)}>
              Close all open ({open.length})
            </Button>
          </>
        }
      />
      {isCurrent && (
        <p className="mb-4 text-sm text-muted-foreground">{fmtPeriod(period)} is still running — it can be closed once it has ended.</p>
      )}

      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : !isLoading && data?.length === 0 ? (
        <EmptyState title={`No brands existed in ${fmtPeriod(period)}`} />
      ) : (
        <Table>
          <THead>
            <Th>Brand</Th>
            <Th>Status</Th>
            <Th right>Total due (USD)</Th>
            <Th>Checks</Th>
            <Th right>Actions</Th>
          </THead>
          {isLoading ? (
            <SkeletonRows cols={5} />
          ) : (
            <tbody>
              {data!.map((r) => (
                <Tr key={r.partnerId}>
                  <Td className="font-medium">
                    <Link to={`/admin/partners/${r.slug}`} className="hover:underline">
                      {r.name}
                    </Link>
                    {r.groupName && <span className="block text-xs font-normal text-muted-foreground">{r.groupName}</span>}
                  </Td>
                  <Td>
                    {r.closed ? (
                      <span>
                        <Badge variant="active">Closed</Badge>
                        <span className="ml-2 text-xs text-muted-foreground">{fmtDateTime(r.closedAt)}</span>
                      </span>
                    ) : (
                      <Badge variant="pending">Open</Badge>
                    )}
                  </Td>
                  <Td right>{r.totalUsdMinor === null ? '—' : fmtMoney(r.totalUsdMinor, 'USD')}</Td>
                  <Td>
                    {r.ungranted > 0 ? (
                      <Link to={`/p/${r.slug}/charges`} className="text-sm text-ata-orange hover:underline">
                        {fmtNum(r.ungranted)} transaction{r.ungranted === 1 ? '' : 's'} not yet applied on the platform
                      </Link>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td right>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setViewing(r)}>
                        {r.closed ? 'Statement' : 'Preview'}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setAdjusting(r)}>
                        Adjustments
                      </Button>
                      {!r.closed && !isCurrent && (
                        <ConfirmCloseButton name={r.name} period={period} onConfirm={() => run(r.slug)} />
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      )}

      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title={`Close ${fmtPeriod(period)} for ${open.length} brand${open.length === 1 ? '' : 's'}?`}
        confirmLabel="Close all"
        destructive={false}
        onConfirm={async () => {
          await run()
          setConfirmAll(false)
        }}
      >
        <p>Each brand's figures and currency rate are frozen and its statement produced. This can't be undone; later changes appear on next month's statement.</p>
      </ConfirmDialog>
      {viewing && <StatementDialog row={viewing} period={period} onClose={() => setViewing(null)} />}
      {adjusting && <AdjustmentsDialog row={adjusting} period={period} onClose={() => setAdjusting(null)} />}
    </>
  )
}

function ConfirmCloseButton({ name, period, onConfirm }: { name: string; period: string; onConfirm: () => Promise<void> }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Close
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Close ${fmtPeriod(period)} for ${name}?`}
        confirmLabel="Close period"
        destructive={false}
        onConfirm={async () => {
          await onConfirm()
          setOpen(false)
        }}
      >
        <p>The figures and currency rate are frozen and the statement produced. This can't be undone.</p>
      </ConfirmDialog>
    </>
  )
}

function StatementDialog({ row, period, onClose }: { row: CloseStatusRow; period: string; onClose: () => void }) {
  const { data, isError, error } = useStatementQuery({ slug: row.slug, period })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`${row.name} — ${fmtPeriod(period)}`} className="max-h-[90vh] max-w-4xl overflow-y-auto">
        {isError ? (
          <FieldError>{apiError(error)}</FieldError>
        ) : !data ? (
          <div className="h-60 animate-pulse rounded-xl bg-muted" />
        ) : (
          <StatementView statement={data} downloadBase={`/api/partners/${row.slug}/statements/${period}`} />
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Manual lines in USD. They go on an open period; for a closed one they go on the
 * earliest open period as a restatement of it.
 */
function AdjustmentsDialog({ row, period, onClose }: { row: CloseStatusRow; period: string; onClose: () => void }) {
  const { data } = useAdjustmentsQuery(row.slug)
  const [add, { isLoading }] = useAddAdjustmentMutation()
  const [remove] = useRemoveAdjustmentMutation()
  const target = row.closed ? recentPeriods(1)[0] : period
  const [kind, setKind] = useState<AdjustmentKind>(row.closed ? 'restatement' : 'service_credit')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [credit, setCredit] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const cents = Math.round(parseFloat(amount) * 100)
    if (!Number.isFinite(cents) || cents <= 0) return setError('Enter an amount in USD, e.g. 125.00')
    try {
      await add({
        slug: row.slug,
        period: target,
        kind,
        description,
        amountMinor: credit ? -cents : cents,
        restatesPeriod: row.closed ? period : null,
      }).unwrap()
      toast.success('Adjustment added')
      setDescription('')
      setAmount('')
    } catch (err) {
      setError(apiError(err))
    }
  }

  const relevant = data?.filter((a) => a.period === target || a.restatesPeriod === period) ?? []

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Adjustments — ${row.name}`}
        description={
          row.closed
            ? `${fmtPeriod(period)} is closed. New lines go on ${fmtPeriod(target)}'s statement as relating to ${fmtPeriod(period)}.`
            : `Lines added here appear on ${fmtPeriod(period)}'s statement, in USD.`
        }
        className="max-w-xl"
      >
        {relevant.length > 0 && (
          <ul className="mb-4 space-y-2 text-sm">
            {relevant.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-border px-3 py-2">
                <div>
                  <div className="font-medium">
                    {KIND_LABELS[a.kind]} · {fmtMoney(a.amountMinor, 'USD')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {a.description} — {fmtPeriod(a.period)}
                    {a.restatesPeriod && `, relates to ${fmtPeriod(a.restatesPeriod)}`} · {a.createdByEmail ?? 'system'}
                  </div>
                </div>
                {a.period === target && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-ata-red"
                    onClick={async () => {
                      try {
                        await remove({ slug: row.slug, id: a.id }).unwrap()
                      } catch (err) {
                        toast.error(apiError(err))
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kind</Label>
              <Select
                ariaLabel="Kind"
                value={kind}
                onValueChange={(v) => setKind(v as AdjustmentKind)}
                options={(Object.keys(KIND_LABELS) as AdjustmentKind[]).map((k) => ({ value: k, label: KIND_LABELS[k] }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Direction</Label>
              <Select
                ariaLabel="Direction"
                value={credit ? 'credit' : 'charge'}
                onValueChange={(v) => setCredit(v === 'credit')}
                options={[
                  { value: 'credit', label: 'Credit to partner' },
                  { value: 'charge', label: 'Extra charge' },
                ]}
                className="w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="adj-reason">Reason</Label>
              <Input id="adj-reason" required value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="adj-amount">Amount (USD)</Label>
              <Input id="adj-amount" required inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Add adjustment
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
